import { Suspense, lazy } from 'react'

import { Box } from '@chakra-ui/react'

import { Spinner } from '@/components/ui'

import type { LiveKitVideoPlayerIProps } from './LiveKitVideoPlayer'
import { VideoChunkBoundary } from './VideoChunkBoundary'
import { VideoChunkSurface } from './VideoChunkSurface'

/**
 * Code-split boundary for `LiveKitVideoPlayer`. `livekit-client` and its
 * dependency tree (`@livekit/protocol`, `@bufbuild/protobuf`, `webrtc-adapter`,
 * `sdp-transform`) are ~1.3MB unminified, and the video-player barrel is
 * imported app-wide — so a static export would ship all of it to every tenant
 * whether or not `FeatureFlag.lumanaCameraStream` is on. Behind this boundary
 * the chunk is fetched only when a Lumana camera actually renders, which makes
 * the flag gate the download too.
 *
 * The type-only import above is erased at compile time, so it does not defeat
 * the split. Everything the barrel exports must stay type-only for that reason.
 */
const LiveKitVideoPlayerImpl = lazy(() =>
  import('./LiveKitVideoPlayer').then((module) => ({
    default: module.LiveKitVideoPlayer,
  }))
)

export const LazyLiveKitVideoPlayer = (props: LiveKitVideoPlayerIProps) => (
  // A rejected import() throws during render, and this app has no error
  // boundary — see VideoChunkBoundary for why that costs the whole page.
  <VideoChunkBoundary
    borderRadius={props.borderRadius}
    placeholderHeight={props.placeholderHeight}
    placeholderWidth={props.placeholderWidth}
  >
    <Suspense
      fallback={
        // Same black box and spinner the player itself shows while connecting,
        // so fetching the chunk doesn't add a layout jump of its own.
        <VideoChunkSurface
          borderRadius={props.borderRadius}
          placeholderHeight={props.placeholderHeight}
          placeholderWidth={props.placeholderWidth}
        >
          <Box
            alignItems='center'
            display='flex'
            height='100%'
            justifyContent='center'
            left={0}
            position='absolute'
            top={0}
            width='100%'
          >
            <Spinner size='lg' />
          </Box>
        </VideoChunkSurface>
      }
    >
      <LiveKitVideoPlayerImpl {...props} />
    </Suspense>
  </VideoChunkBoundary>
)
